import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchBlogPost } from "../lib/api";

const BlogDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchBlogPost(slug, lang)
      .then((data) => setPost(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (loading) {
    return (
      <div className="container-rotam py-32" data-testid="blog-detail-loading">
        <div className="h-12 w-2/3 bg-rotam-border/40 animate-pulse rounded mb-6" />
        <div className="h-4 w-1/3 bg-rotam-border/40 animate-pulse rounded mb-12" />
        <div className="h-80 bg-rotam-border/40 animate-pulse rounded" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="container-rotam py-32 text-center" data-testid="blog-not-found">
        <p className="text-rotam-muted">{t.blog.notFound}</p>
        <Link to="/blog" className="btn-primary mt-8 inline-flex">
          <ArrowLeft size={16} /> {t.blog.backToBlog}
        </Link>
      </div>
    );
  }

  return (
    <article data-testid="blog-detail-page">
      <section className="pt-16 md:pt-24 pb-12">
        <div className="container-rotam max-w-3xl">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm text-rotam-primary hover:text-rotam-primary-hover"
          >
            <ArrowLeft size={14} /> {t.blog.backToBlog}
          </Link>
          <div className="mt-6 flex items-center gap-4 text-[11px] tracking-[0.18em] uppercase text-rotam-muted">
            <span className="flex items-center gap-1.5">
              <Calendar size={12} /> {formatDate(post.published_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={12} /> {post.read_minutes} {t.blog.minRead}
            </span>
          </div>
          <h1 className="mt-4 font-heading font-light text-4xl md:text-5xl leading-[1.1] tracking-tight">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-rotam-muted leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      <section className="pb-12">
        <div className="container-rotam">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-[300px] md:h-[520px] object-cover rounded-sm"
          />
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-rotam max-w-3xl">
          <div className="prose prose-lg max-w-none text-rotam-ink leading-relaxed space-y-6">
            {post.content.split("\n\n").map((para, i) =>
              para.startsWith("## ") ? (
                <h2 key={i} className="font-heading text-2xl md:text-3xl font-medium mt-12 mb-4">
                  {para.replace("## ", "")}
                </h2>
              ) : (
                <p key={i} className="text-rotam-muted leading-[1.85]">
                  {para}
                </p>
              )
            )}
          </div>
        </div>
      </section>
    </article>
  );
};

export default BlogDetail;
